import { logger } from '@/utils/logger';
import { RECOVERY_STATE_VERSION, RECOVERY_STORAGE_KEY, decideRecovery, parseRecoveryState } from './lazyImportRecovery';

interface VitePreloadErrorEvent extends Event {
  payload?: unknown;
}

interface VitePreloadErrorRecoveryDependencies {
  now: () => number;
  getOnline: () => boolean;
  getRoute: () => string;
  getRelease: () => string;
  getStoredRecoveryState: () => string | null;
  setStoredRecoveryState: (value: string) => void;
  reload: () => void;
}

const defaultDependencies: VitePreloadErrorRecoveryDependencies = {
  now: () => Date.now(),
  getOnline: () => navigator.onLine,
  getRoute: () => window.location.pathname,
  getRelease: () => globalThis.__APP_RELEASE__ ?? '',
  getStoredRecoveryState: () => sessionStorage.getItem(RECOVERY_STORAGE_KEY),
  setStoredRecoveryState: (value) => sessionStorage.setItem(RECOVERY_STORAGE_KEY, value),
  reload: () => window.location.reload(),
};

function toError(payload: unknown): Error {
  if (payload instanceof Error) {
    return payload;
  }
  if (typeof payload === 'string') {
    return new Error(payload);
  }
  return new Error('Vite preload error without Error payload');
}

export function handleVitePreloadError(
  event: VitePreloadErrorEvent,
  dependencies: VitePreloadErrorRecoveryDependencies = defaultDependencies
) {
  const error = toError(event.payload);
  const now = dependencies.now();
  const online = dependencies.getOnline();
  const route = dependencies.getRoute();
  const release = dependencies.getRelease();
  const stored = parseRecoveryState(dependencies.getStoredRecoveryState());
  const action = decideRecovery({ now, online, stored, error });

  if (action.type !== 'reload') {
    return;
  }

  event.preventDefault();

  const recoveryContext = {
    category: action.category,
    route,
    release,
    online,
    source: 'vite:preloadError',
  };

  dependencies.setStoredRecoveryState(
    JSON.stringify({
      v: RECOVERY_STATE_VERSION,
      attemptedAt: now,
      category: action.category,
      route,
      release,
    })
  );
  logger.info('lazy_import.recovered', recoveryContext);
  dependencies.reload();
}

export function registerVitePreloadErrorRecovery() {
  window.addEventListener('vite:preloadError', handleVitePreloadError);
}
